import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FolderOpen,
  MessageSquare,
  History,
  TrendingUp,
  TrendingDown,
  Search,
  Activity,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  Sparkles,
  RefreshCw,
  Filter,
  Globe
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { format, parseISO, subDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import axios from 'axios';
import './DashboardHome.css';

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    const saved = localStorage.getItem('selectedProject');
    return (saved === 'unassigned') ? 'all' : (saved || 'all');
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Состояние для фильтра по датам
  const [dateFrom, setDateFrom] = useState(() => {
    return format(subDays(new Date(), 30), 'yyyy-MM-dd');
  });
  const [dateTo, setDateTo] = useState(() => {
    return format(new Date(), 'yyyy-MM-dd');
  });

  // Загрузка списка проектов
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  }, []);

  const fetchDashboardData = useCallback(async (silent = false, projectId, fromDate, toDate) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const params = new URLSearchParams();
      if (projectId && projectId !== 'all') params.append('projectId', projectId);
      if (fromDate) params.append('date_from', fromDate);
      if (toDate) params.append('date_to', toDate);

      const queryString = params.toString();
      const statsUrl = `/api/dashboard/stats${queryString ? `?${queryString}` : ''}`;
      const trendsUrl = `/api/dashboard/trends${queryString ? `?${queryString}` : ''}`;

      const [statsRes, trendsRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(trendsUrl)
      ]);

      setStats(statsRes.data);
      setTrends(trendsRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchDashboardData(false, selectedProjectId, dateFrom, dateTo);
  }, [selectedProjectId, dateFrom, dateTo, fetchDashboardData]);

  const handleProjectChange = (e) => {
    const newProjectId = e.target.value;
    setSelectedProjectId(newProjectId);
    localStorage.setItem('selectedProject', newProjectId);
  };

  // Цвета для графиков
  const COLORS = {
    primary: '#667eea',
    secondary: '#764ba2',
    google: '#4285F4',
    yandex: '#FC3F1D',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6'
  };

  const BRAND_SCORE_COLORS = [
    '#10b981', // Найден
    '#ef4444'  // Не найден
  ];

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'd MMM', { locale: ru });
    } catch {
      return dateString;
    }
  };

  const getBrandScoreColor = (score) => {
    return score >= 1 ? COLORS.success : COLORS.danger;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="status-icon success" />;
      case 'error':
        return <XCircle size={16} className="status-icon error" />;
      case 'running':
        return <RefreshCw size={16} className="status-icon running" />;
      default:
        return <Clock size={16} className="status-icon pending" />;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-home">
        <div className="loading-container">
          <RefreshCw size={32} className="loading-spinner" />
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-home">
        <div className="error-container">
          <AlertCircle size={48} />
          <p>{error}</p>
          <button onClick={() => fetchDashboardData()} className="retry-button">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const hasData = trends?.checksTrend?.length > 0;

  const selectedProject = projects.find(p => p.id === parseInt(selectedProjectId));

  return (
    <div className="dashboard-home">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Дашборд</h1>
          <p>Обзор активности и аналитика по поисковой выдаче</p>
        </div>
        <div className="header-actions">
          <div className="date-filter">
            <Clock size={18} />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
            />
            <span className="date-separator">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="project-filter">
            <Filter size={18} />
            <select
              value={selectedProjectId}
              onChange={handleProjectChange}
              className="project-select"
            >
              <option value="all">Все проекты</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>
          <button
            className="refresh-button"
            onClick={() => fetchDashboardData(true, selectedProjectId, dateFrom, dateTo)}
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
            Обновить
          </button>
        </div>
      </div>

      {/* Индикатор выбранного проекта */}
      {selectedProjectId !== 'all' && selectedProject && (
        <div className="selected-project-badge">
          <FolderOpen size={16} />
          <span>Фильтр по проекту: <strong>{selectedProject.title}</strong></span>
          <button
            className="clear-filter-btn"
            onClick={() => setSelectedProjectId('all')}
            title="Сбросить фильтр"
          >
            ✕
          </button>
        </div>
      )}

      {/* Основные метрики */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#667eea20', color: COLORS.primary }}>
            <FolderOpen size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats?.projects || 0}</div>
            <div className="stat-title">Проекты</div>
            <div className="stat-description">Активных проектов</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#10b98120', color: COLORS.success }}>
            <MessageSquare size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats?.requests?.total || 0}</div>
            <div className="stat-title">Запросы</div>
            <div className="stat-description">
              <span className="google-badge">G: {stats?.requests?.google || 0}</span>
              <span className="yandex-badge">Я: {stats?.requests?.yandex || 0}</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#f59e0b20', color: COLORS.warning }}>
            <History size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats?.checks?.total || 0}</div>
            <div className="stat-title">Проверки</div>
            <div className="stat-description">Сегодня: {stats?.checks?.today || 0}</div>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon" style={{ backgroundColor: '#764ba220', color: COLORS.secondary }}>
            <Sparkles size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">
              {stats?.brandMention?.checked > 0
                ? `${stats.brandMention.found}/${stats.brandMention.checked}`
                : '—'}
            </div>
            <div className="stat-title">Бренд в выдаче</div>
            <div className="stat-description">Найден / Проверено</div>
          </div>
          {stats?.brandMention?.checked > 0 && (
            <div
              className="score-indicator"
              style={{ backgroundColor: stats.brandMention.found > 0 ? COLORS.success : COLORS.danger }}
            />
          )}
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#10b98120', color: '#10b981' }}>
            <Globe size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">
              {stats?.siteMention?.checked > 0
                ? `${stats.siteMention.found}/${stats.siteMention.checked}`
                : '—'}
            </div>
            <div className="stat-title">Сайт в выдаче</div>
            <div className="stat-description">
              {stats?.siteMention?.checked > 0
                ? `${Math.round(stats.siteMention.found / stats.siteMention.checked * 100)}% найдено`
                : 'Нет данных'}
            </div>
          </div>
        </div>
      </div>

      {hasData ? (
        <>
          {/* Графики трендов */}
          <div className="charts-section">
            <div className="chart-card large">
              <div className="chart-header">
                <h3><Activity size={20} /> Тренд проверок и Brand Score</h3>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trends.checksTrend}>
                    <defs>
                      <linearGradient id="colorChecks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      stroke="#9ca3af"
                      fontSize={12}
                    />
                    <YAxis yAxisId="left" stroke="#9ca3af" fontSize={12} label={{ value: 'Количество', angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: '12px' } }} />
                    <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={12} domain={[0, 100]} />
                    <Tooltip
                      labelFormatter={(value) => formatDate(value)}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="checksCount"
                      name="Проверки"
                      stroke={COLORS.primary}
                      fill="url(#colorChecks)"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="brandFound"
                      name="Бренд найден"
                      stroke={COLORS.success}
                      strokeWidth={2}
                      dot={{ fill: COLORS.success, strokeWidth: 2 }}
                      connectNulls
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3><BarChart3 size={20} /> Бренд в выдаче по поисковикам</h3>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={trends.checksTrend.slice(-14)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      stroke="#9ca3af"
                      fontSize={12}
                    />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip
                      labelFormatter={(value) => formatDate(value)}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="googleBrandFound" name="Google: бренд найден" fill={COLORS.google} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="yandexBrandFound" name="Yandex: бренд найден" fill={COLORS.yandex} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3><TrendingUp size={20} /> Бренд в выдаче</h3>
              </div>
              <div className="chart-container pie-chart">
                {trends.brandScoreDistribution?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={trends.brandScoreDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="count"
                        nameKey="category"
                        label={({ category, count }) => `${count}`}
                      >
                        {trends.brandScoreDistribution.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={BRAND_SCORE_COLORS[index % BRAND_SCORE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data-chart">
                    <p>Нет данных для отображения</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Лидеры и аутсайдеры */}
          <div className="performers-section">
            <div className="performers-card success">
              <div className="performers-header">
                <TrendingUp size={20} />
                <h3>Лучшие запросы</h3>
              </div>
              <div className="performers-list">
                {trends.topPerformers?.length > 0 ? (
                  trends.topPerformers.map((item, index) => (
                    <div key={index} className="performer-item">
                      <div className="performer-rank">{index + 1}</div>
                      <div className="performer-info">
                        <div className="performer-query">{item.query}</div>
                        <div className="performer-meta">
                          <span className={`engine-tag ${item.searchEngine}`}>
                            {item.searchEngine === 'google' ? 'Google' : 'Yandex'}
                          </span>
                          <span className="checks-count">{item.checksCount} проверок</span>
                        </div>
                      </div>
                      <div
                        className="performer-score"
                        style={{ backgroundColor: item.brandFound > 0 ? COLORS.success : COLORS.danger }}
                      >
                        {item.brandFound}/{item.checksCount}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">Пока нет данных</p>
                )}
              </div>
            </div>

            <div className="performers-card danger">
              <div className="performers-header">
                <TrendingDown size={20} />
                <h3>Требуют внимания</h3>
              </div>
              <div className="performers-list">
                {trends.worstPerformers?.length > 0 ? (
                  trends.worstPerformers.map((item, index) => (
                    <div key={index} className="performer-item">
                      <div className="performer-rank">{index + 1}</div>
                      <div className="performer-info">
                        <div className="performer-query">{item.query}</div>
                        <div className="performer-meta">
                          <span className={`engine-tag ${item.searchEngine}`}>
                            {item.searchEngine === 'google' ? 'Google' : 'Yandex'}
                          </span>
                          <span className="checks-count">{item.checksCount} проверок</span>
                        </div>
                      </div>
                      <div
                        className="performer-score"
                        style={{ backgroundColor: item.brandFound > 0 ? COLORS.success : COLORS.danger }}
                      >
                        {item.brandFound}/{item.checksCount}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">Пока нет данных</p>
                )}
              </div>
            </div>
          </div>

          {/* Последние проверки и статистика по проектам */}
          <div className="bottom-section">
            <div className="recent-checks-card">
              <div className="card-header">
                <h3><Clock size={20} /> Последние проверки</h3>
                <Link to="/dashboard/history" className="view-all-link">
                  Все проверки <ArrowRight size={16} />
                </Link>
              </div>
              <div className="recent-checks-list">
                {trends.recentChecks?.length > 0 ? (
                  trends.recentChecks.map((check) => (
                    <div key={check.id} className="recent-check-item">
                      {getStatusIcon(check.status)}
                      <div className="check-info">
                        <div className="check-query">{check.query}</div>
                        <div className="check-meta">
                          <span className={`engine-tag ${check.search_engine}`}>
                            {check.search_engine === 'google' ? 'Google' : 'Yandex'}
                          </span>
                          {check.project_title && (
                            <span className="project-tag">{check.project_title}</span>
                          )}
                        </div>
                      </div>
                      <div className="check-details">
                        {check.brand_score !== null && (
                          <div
                            className="check-score"
                            style={{ backgroundColor: check.brand_score >= 1 ? COLORS.success : COLORS.danger }}
                          >
                            {check.brand_score >= 1 ? '✓' : '✗'}
                          </div>
                        )}
                        <div className="check-time">
                          {format(new Date(check.check_date), 'dd.MM HH:mm', { locale: ru })}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">Пока нет проверок</p>
                )}
              </div>
            </div>

            <div className="projects-stats-card">
              <div className="card-header">
                <h3><FolderOpen size={20} /> Статистика по проектам</h3>
                <Link to="/dashboard/projects" className="view-all-link">
                  Все проекты <ArrowRight size={16} />
                </Link>
              </div>
              <div className="projects-stats-list">
                {trends.projectsStats?.length > 0 ? (
                  trends.projectsStats.map((project) => (
                    <div key={project.id} className="project-stat-item">
                      <div className="project-info">
                        <div className="project-name">{project.title}</div>
                        <div className="project-meta">
                          <span>{project.requestsCount} запросов</span>
                          <span>{project.checksCount} проверок</span>
                        </div>
                      </div>
                      {project.brandChecked > 0 && (
                        <div
                          className="project-score"
                          style={{ backgroundColor: project.brandFound > 0 ? COLORS.success : COLORS.danger }}
                        >
                          {project.brandFound}/{project.brandChecked}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="no-data">Пока нет проектов</p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Пустое состояние - без данных */
        <div className="empty-state-section">
          <div className="empty-state-card">
            <div className="empty-icon">
              <Search size={48} />
            </div>
            <h2>Начните отслеживать поисковую выдачу</h2>
            <p>Создайте проект и добавьте запросы для мониторинга позиций в Google и Яндекс</p>
            <div className="quick-actions">
              <Link to="/dashboard/projects" className="action-button primary">
                <FolderOpen size={20} />
                Создать проект
              </Link>
              <Link to="/dashboard/requests" className="action-button secondary">
                <MessageSquare size={20} />
                Добавить запросы
              </Link>
            </div>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Search size={24} />
              </div>
              <h4>Мониторинг позиций</h4>
              <p>Отслеживайте позиции по ключевым запросам в Google и Яндекс</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <TrendingUp size={24} />
              </div>
              <h4>Анализ трендов</h4>
              <p>Визуализация изменений в поисковой выдаче за любой период</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Sparkles size={24} />
              </div>
              <h4>Вхождение бренда</h4>
              <p>Проверка присутствия вашего бренда в поисковой выдаче</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
